import { PackagedTypeEnum } from '../../properties/PackagedTypeEnum';
import { SecurityRuleTypeEnum } from '../../properties/PropertySecurity';
import { PERMISSION_KEYS } from 'constants-permissions';
import { IntegrationDefinition } from '../IntegrationDefinition';
import {
  IntegrationCategoryEnum,
  IntegrationProviderEnum,
  IntegrationTypeEnum,
} from 'constants-integrations';

function constructOIDCRedirectUrl({
  integrationId,
  thirdPartyRedirectUrl,
  connection,
  organization,
}: {
  integrationId: string;
  thirdPartyRedirectUrl: string | null;
  connection?: string | null;
  /** Auth0 organization id; sent as the `organization` query parameter */
  organization?: string | null;
}) {
  if (!thirdPartyRedirectUrl) return;

  const url = new URL(thirdPartyRedirectUrl);

  url.searchParams.set('ext-integration-id', integrationId);

  // Add connection parameter if provided
  // This tells the third-party service which Auth0 connection to use
  if (connection) {
    url.searchParams.set('connection', connection);
  }

  if (organization) {
    url.searchParams.set('organization', organization);
  }

  return url.toString();
}

const OIDCIntegrationDefinition: IntegrationDefinition = {
  name: IntegrationProviderEnum.OIDC,
  allowsMultipleInstances: true,
  hasCustomUI: true,
  friendlyName: 'OIDC : Auth0 As Identity Provider',
  description:
    'Defines properties for a OIDC integration using Auth0 as the identity provider.',
  category: IntegrationCategoryEnum.Service,
  platforms: [],
  type: IntegrationTypeEnum.WebView,
  settings: {},
  getChannelIntegrationName(integrationData): string {
    return `OIDC - ${integrationData.settings.pageTitle}`;
  },
  properties: {
    pageTitle: {
      friendlyName: 'Page Title',
      type: 'String',
      description: 'The title of the page',
      validators: [
        {
          name: 'Required',
          value: true,
        },
      ],
    },
    description: {
      friendlyName: 'Page Description',
      type: 'LongText',
      validators: [
        {
          name: 'Required',
          value: true,
        },
      ],
    },
    serviceProvider: {
      friendlyName: 'Service Provider',
      packagedType: PackagedTypeEnum.Dropdown,
      type: 'Option',
      validators: [
        {
          name: 'Required',
          value: true,
        },
      ],
    },
    redirectURL: {
      friendlyName: 'Redirect URL',
      type: 'Url',
      description: 'The OIDC service provider redirect URL.',
      validators: [
        {
          name: 'Required',
          value: true,
        },
      ],
    },
    customClaims: {
      friendlyName: 'Custom claims',
      type: 'String',
      description: 'Custom claims fields required by current provider.',
      validators: [],
      secure: {
        read: [
          {
            type: SecurityRuleTypeEnum.ChannelPermission,
            value: PERMISSION_KEYS.PERMISSION_INTEGRATION_READ,
          },
        ],
      },
    },
  },
  mobilePermissions: [],
};

export { OIDCIntegrationDefinition, constructOIDCRedirectUrl };
export const definition = OIDCIntegrationDefinition;
